import React, {useCallback, useEffect, useMemo, useState} from "react";
import {observer} from "mobx-react-lite";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import {SequenceApplicationStore} from "./store/SequenceApplicationStore";
import {SequenceStateStore} from "./store/SequenceStateStore";
import {ProjectAssetsStore} from "../state/ProjectAssetsStore";
import {SequenceCompositionService} from "./services/SequenceCompositionService";
import {useAssetStore} from "../storage/AssetStore";

type TranscriptWord = {
    text: string;
    token: string;
    startSec: number;
    endSec: number;
};

type ClipSegment = {
    srcStartSec: number;
    srcEndSec: number;
    compStartSec: number;
};

type CandidateWord = {
    token: string;
    text: string;
    sourceStartSec: number;
    compStartSec: number;
    order: number;
    useful: boolean;
};

type ClipAlignmentContext = {
    clipId: string;
    clip: any;
    displayName: string;
    trackId: string;
    transcriptId: string;
    candidates: CandidateWord[];
};

type AnchorMatch = {
    token: string;
    matchesByClipId: Record<string, CandidateWord>;
};

type SourceGroup = {
    id: string;
    contexts: ClipAlignmentContext[];
};

type GroupOrderingProfile = {
    groupId: string;
    contexts: ClipAlignmentContext[];
    sourceSec: number;
    compSec: number;
};

type AlignmentMoveSummary = {
    movedClipCount: number;
    constrainedClipCount: number;
};

type SemanticAlignmentPlan = {
    groups?: Array<{ clipIds: string[] }>;
    conversationOrder?: string[];
    reason?: string;
};

const STOP_WORDS = new Set([
    "a", "an", "and", "are", "as", "at", "be", "but", "by", "for", "from", "had",
    "has", "have", "he", "her", "his", "i", "if", "in", "is", "it", "its", "me",
    "my", "not", "of", "on", "or", "our", "she", "so", "that", "the", "their", "them",
    "they", "this", "to", "was", "we", "were", "will", "with", "you", "your",
]);

const PANEL_BASE_SX = {
    display: "flex",
    flexDirection: "column",
    background: "#ffffff",
    border: "0.5px solid #E5E7EB",
    borderRadius: "12px",
    px: 1,
    py: 1,
};

const normalizeToken = (value: string): string => {
    return String(value ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9']+/g, "")
        .trim();
};

const EMPTY_MOVE_SUMMARY: AlignmentMoveSummary = {
    movedClipCount: 0,
    constrainedClipCount: 0,
};

const EPSILON_MOVE_SEC = 0.002;
const EPSILON_CONSTRAINED_SEC = 0.025;

const toFiniteNumber = (value: any, fallback: number): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const isUsefulToken = (token: string): boolean => {
    if (!token) return false;
    if (token.length < 3) return false;
    if (/^\d+$/.test(token)) return false;
    return !STOP_WORDS.has(token);
};

const extractTranscriptWords = (transcriptJson: any): TranscriptWord[] => {
    if (!Array.isArray(transcriptJson?.words)) return [];

    const words: TranscriptWord[] = [];
    const MIN_ENERGY_RATIO = 0.2;
    for (const word of transcriptJson.words) {
        if ((word?.type ?? "word") !== "word") continue;
        const energyRatio = Number(word?.energy?.ratio);
        if (Number.isFinite(energyRatio) && energyRatio > 0 && energyRatio < MIN_ENERGY_RATIO) continue;

        const text = String(word?.text ?? word?.word ?? "").trim();
        const token = normalizeToken(text);
        if (!token) continue;

        const startSec = toFiniteNumber(word?.t0 ?? word?.start ?? word?.startSec, Number.NaN);
        if (!Number.isFinite(startSec) || startSec < 0) continue;
        const endSec = toFiniteNumber(word?.t1 ?? word?.end ?? word?.endSec, startSec);

        words.push({ text, token, startSec, endSec });
    }

    words.sort((a, b) => a.startSec - b.startSec);
    return words;
};

const getClipSegments = (clip: any): ClipSegment[] => {
    const derivedSegments = Array.isArray(clip?.segments)
        ? clip.segments
            .map((segment: any) => ({
                srcStartSec: toFiniteNumber(segment?.srcStartSec, Number.NaN),
                srcEndSec: toFiniteNumber(segment?.srcEndSec, Number.NaN),
                compStartSec: toFiniteNumber(segment?.compStartSec, Number.NaN),
            }))
            .filter((segment: ClipSegment) =>
                Number.isFinite(segment.srcStartSec) &&
                Number.isFinite(segment.srcEndSec) &&
                Number.isFinite(segment.compStartSec) &&
                segment.srcEndSec > segment.srcStartSec
            )
        : [];

    if (derivedSegments.length > 0) {
        derivedSegments.sort((a: ClipSegment, b: ClipSegment) => a.compStartSec - b.compStartSec);
        return derivedSegments;
    }

    const inSec = toFiniteNumber(clip?.range?.[0]?.seconds, 0);
    const outSec = toFiniteNumber(clip?.range?.[1]?.seconds, inSec);
    if (!(outSec > inSec)) return [];

    const startSec = toFiniteNumber(clip?.start?.seconds, toFiniteNumber(clip?.offset?.seconds, 0) + inSec);
    return [{
        srcStartSec: inSec,
        srcEndSec: outSec,
        compStartSec: startSec,
    }];
};

const sourceTimeToCompositionTime = (clip: any, sourceSec: number, segments: ClipSegment[]): number | null => {
    if (!Number.isFinite(sourceSec)) return null;
    const playbackRate = Math.max(0.0001, toFiniteNumber(clip?.playbackRate, 1));
    for (const segment of segments) {
        if (sourceSec < segment.srcStartSec || sourceSec > segment.srcEndSec) continue;
        const relativeSourceSec = sourceSec - segment.srcStartSec;
        return segment.compStartSec + (relativeSourceSec / playbackRate);
    }
    return null;
};

const collectCandidateWords = (clip: any, transcriptWords: TranscriptWord[]): CandidateWord[] => {
    const clipStartSec = toFiniteNumber(clip?.start?.seconds, 0);
    const segments = getClipSegments(clip);
    if (segments.length === 0) return [];

    const MAX_CANDIDATES = 220;
    const MAX_RELATIVE_SEC = 45;

    const candidates: CandidateWord[] = [];
    for (const word of transcriptWords) {
        const compStartSec = sourceTimeToCompositionTime(clip, word.startSec, segments);
        if (compStartSec == null) continue;

        const relativeSec = compStartSec - clipStartSec;
        if (relativeSec < -0.001) continue;
        if (relativeSec > MAX_RELATIVE_SEC && candidates.length > 30) break;

        candidates.push({
            token: word.token,
            text: word.text,
            sourceStartSec: word.startSec,
            compStartSec,
            order: candidates.length,
            useful: isUsefulToken(word.token),
        });

        if (candidates.length >= MAX_CANDIDATES) break;
    }

    return candidates;
};

const median = (values: number[]): number => {
    if (!values.length) return Number.NaN;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 1) return sorted[mid];
    return (sorted[mid - 1] + sorted[mid]) / 2;
};

const combineMoveSummaries = (a: AlignmentMoveSummary, b: AlignmentMoveSummary): AlignmentMoveSummary => ({
    movedClipCount: a.movedClipCount + b.movedClipCount,
    constrainedClipCount: a.constrainedClipCount + b.constrainedClipCount,
});

const getCurrentClipStartSec = (clip: any): number => {
    return toFiniteNumber(clip?.start?.seconds, 0);
};

const moveContextByDelta = (context: ClipAlignmentContext, deltaSec: number): AlignmentMoveSummary => {
    if (!Number.isFinite(deltaSec) || Math.abs(deltaSec) <= EPSILON_MOVE_SEC) {
        return EMPTY_MOVE_SUMMARY;
    }

    const currentStartSec = getCurrentClipStartSec(context.clip);
    const desiredStartSec = currentStartSec + deltaSec;
    const actualStartSec = SequenceCompositionService.moveClipTo(context.clipId, desiredStartSec);
    const actualDelta = actualStartSec - currentStartSec;

    return {
        movedClipCount: Math.abs(actualDelta) > EPSILON_MOVE_SEC ? 1 : 0,
        constrainedClipCount: Math.abs(actualDelta - deltaSec) > EPSILON_CONSTRAINED_SEC ? 1 : 0,
    };
};

const getUsefulTokenSet = (context: ClipAlignmentContext, limit = 90): Set<string> => {
    const set = new Set<string>();
    for (const candidate of context.candidates) {
        if (!candidate.useful) continue;
        set.add(candidate.token);
        if (set.size >= limit) break;
    }
    return set;
};

const computeTokenMatchStats = (a: ClipAlignmentContext, b: ClipAlignmentContext): { sharedCount: number; containment: number } => {
    const setA = getUsefulTokenSet(a);
    const setB = getUsefulTokenSet(b);
    if (setA.size === 0 || setB.size === 0) {
        return { sharedCount: 0, containment: 0 };
    }
    let sharedCount = 0;
    for (const token of setA) {
        if (setB.has(token)) sharedCount += 1;
    }
    return {
        sharedCount,
        containment: sharedCount / Math.max(1, Math.min(setA.size, setB.size)),
    };
};

const shouldMergeAsSameSource = (a: ClipAlignmentContext, b: ClipAlignmentContext): boolean => {
    if (a.trackId && b.trackId && a.trackId === b.trackId) return true;
    if (a.transcriptId && b.transcriptId && a.transcriptId === b.transcriptId) return true;

    const tokenStats = computeTokenMatchStats(a, b);
    const pairAnchor =
        findSharedAnchor([a, b], { requireUseful: true }) ??
        findSharedAnchor([a, b], { requireUseful: false });

    const firstA = pairAnchor?.matchesByClipId[a.clipId];
    const firstB = pairAnchor?.matchesByClipId[b.clipId];
    const hasStrongEarlyPairAnchor = Boolean(
        pairAnchor &&
        firstA &&
        firstB &&
        Math.max(firstA.order, firstB.order) <= 14 &&
        Math.abs(firstA.sourceStartSec - firstB.sourceStartSec) <= 16
    );

    if (tokenStats.sharedCount >= 10 && tokenStats.containment >= 0.35) return true;
    if (tokenStats.sharedCount >= 6 && tokenStats.containment >= 0.25 && hasStrongEarlyPairAnchor) return true;
    return false;
};

const inferSourceGroupsHeuristically = (contexts: ClipAlignmentContext[]): SourceGroup[] => {
    if (contexts.length === 0) return [];
    if (contexts.length === 1) {
        return [{ id: `group-${contexts[0].clipId}`, contexts: [contexts[0]] }];
    }

    const parentByClipId = new Map<string, string>();
    for (const context of contexts) {
        parentByClipId.set(context.clipId, context.clipId);
    }

    const find = (clipId: string): string => {
        let current = parentByClipId.get(clipId) ?? clipId;
        while (parentByClipId.get(current) !== current) {
            current = parentByClipId.get(current) ?? current;
        }
        let pathNode = clipId;
        while (parentByClipId.get(pathNode) !== current) {
            const next = parentByClipId.get(pathNode) ?? current;
            parentByClipId.set(pathNode, current);
            pathNode = next;
        }
        return current;
    };

    const unite = (leftClipId: string, rightClipId: string): void => {
        const leftRoot = find(leftClipId);
        const rightRoot = find(rightClipId);
        if (leftRoot === rightRoot) return;
        parentByClipId.set(rightRoot, leftRoot);
    };

    for (let i = 0; i < contexts.length; i++) {
        for (let j = i + 1; j < contexts.length; j++) {
            if (shouldMergeAsSameSource(contexts[i], contexts[j])) {
                unite(contexts[i].clipId, contexts[j].clipId);
            }
        }
    }

    const grouped = new Map<string, ClipAlignmentContext[]>();
    for (const context of contexts) {
        const root = find(context.clipId);
        const list = grouped.get(root) ?? [];
        list.push(context);
        grouped.set(root, list);
    }

    return Array.from(grouped.values())
        .sort((a, b) => b.length - a.length)
        .map((groupContexts, index) => ({
            id: `group-${index + 1}`,
            contexts: groupContexts,
        }));
};

const applySemanticGroupingPlan = (
    contexts: ClipAlignmentContext[],
    semanticPlan: SemanticAlignmentPlan | null
): SourceGroup[] | null => {
    if (!semanticPlan?.groups || semanticPlan.groups.length === 0) return null;

    const byClipId = new Map<string, ClipAlignmentContext>();
    for (const context of contexts) {
        byClipId.set(context.clipId, context);
    }

    const used = new Set<string>();
    const grouped: SourceGroup[] = [];

    for (let index = 0; index < semanticPlan.groups.length; index++) {
        const group = semanticPlan.groups[index];
        const clipIds = Array.from(new Set((group?.clipIds ?? []).filter(Boolean)));
        if (clipIds.length === 0) continue;

        const groupContexts: ClipAlignmentContext[] = [];
        for (const clipId of clipIds) {
            const context = byClipId.get(clipId);
            if (!context) continue;
            if (used.has(clipId)) continue;
            used.add(clipId);
            groupContexts.push(context);
        }

        if (groupContexts.length > 0) {
            grouped.push({
                id: `semantic-group-${index + 1}`,
                contexts: groupContexts,
            });
        }
    }

    for (const context of contexts) {
        if (used.has(context.clipId)) continue;
        grouped.push({
            id: `semantic-group-extra-${context.clipId}`,
            contexts: [context],
        });
    }

    return grouped.length > 0 ? grouped : null;
};

const buildSourceGroups = (contexts: ClipAlignmentContext[], semanticPlan: SemanticAlignmentPlan | null): SourceGroup[] => {
    const semanticGroups = applySemanticGroupingPlan(contexts, semanticPlan);
    if (semanticGroups) return semanticGroups;
    return inferSourceGroupsHeuristically(contexts);
};

const getOrderingCandidate = (context: ClipAlignmentContext): CandidateWord | null => {
    const useful = context.candidates.find((candidate) => candidate.useful);
    if (useful) return useful;
    return context.candidates[0] ?? null;
};

const getCandidateCompStartAtCurrentPosition = (context: ClipAlignmentContext, candidate: CandidateWord): number | null => {
    const segments = getClipSegments(context.clip);
    if (segments.length === 0) return null;
    return sourceTimeToCompositionTime(context.clip, candidate.sourceStartSec, segments);
};

const getGroupOrderingProfile = (group: SourceGroup): GroupOrderingProfile | null => {
    const sourceTimes: number[] = [];
    const compositionTimes: number[] = [];

    for (const context of group.contexts) {
        const orderingCandidate = getOrderingCandidate(context);
        if (!orderingCandidate) continue;
        const compStartSec = getCandidateCompStartAtCurrentPosition(context, orderingCandidate);
        if (compStartSec == null) continue;
        sourceTimes.push(orderingCandidate.sourceStartSec);
        compositionTimes.push(compStartSec);
    }

    if (sourceTimes.length === 0 || compositionTimes.length === 0) return null;

    return {
        groupId: group.id,
        contexts: group.contexts,
        sourceSec: median(sourceTimes),
        compSec: median(compositionTimes),
    };
};

const resolveConversationOrder = (
    profiles: GroupOrderingProfile[],
    semanticPlan: SemanticAlignmentPlan | null
): GroupOrderingProfile[] => {
    if (profiles.length <= 1) return profiles;
    const orderedByHeuristic = [...profiles].sort((a, b) => {
        if (a.sourceSec !== b.sourceSec) return a.sourceSec - b.sourceSec;
        return a.compSec - b.compSec;
    });

    const semanticOrder = semanticPlan?.conversationOrder ?? [];
    if (semanticOrder.length === 0) return orderedByHeuristic;

    const byGroupId = new Map<string, GroupOrderingProfile>();
    for (const profile of orderedByHeuristic) {
        byGroupId.set(profile.groupId, profile);
    }

    const ordered: GroupOrderingProfile[] = [];
    for (const groupId of semanticOrder) {
        const profile = byGroupId.get(groupId);
        if (!profile) continue;
        ordered.push(profile);
        byGroupId.delete(groupId);
    }

    if (ordered.length === 0) return orderedByHeuristic;
    const remaining = [...byGroupId.values()].sort((a, b) => a.sourceSec - b.sourceSec);
    return [...ordered, ...remaining];
};

const alignSameSourceGroup = (
    group: SourceGroup
): { summary: AlignmentMoveSummary; anchorToken: string | null; error: string | null } => {
    if (group.contexts.length < 2) {
        return { summary: EMPTY_MOVE_SUMMARY, anchorToken: null, error: null };
    }

    const sharedAnchor =
        findSharedAnchor(group.contexts, { requireUseful: true }) ??
        findSharedAnchor(group.contexts, { requireUseful: false });
    if (!sharedAnchor) {
        return {
            summary: EMPTY_MOVE_SUMMARY,
            anchorToken: null,
            error: "No shared timing word found for clips that appear to be the same source.",
        };
    }

    const anchorCompTimes = group.contexts.map(
        (context) => sharedAnchor.matchesByClipId[context.clipId]?.compStartSec ?? Number.NaN
    );
    if (anchorCompTimes.some((value) => !Number.isFinite(value))) {
        return {
            summary: EMPTY_MOVE_SUMMARY,
            anchorToken: null,
            error: "Missing anchor timing data for one or more clips.",
        };
    }

    const targetAnchorCompSec = Math.max(...anchorCompTimes);
    let summary = EMPTY_MOVE_SUMMARY;

    for (const context of group.contexts) {
        const match = sharedAnchor.matchesByClipId[context.clipId];
        if (!match) continue;
        const deltaSec = targetAnchorCompSec - match.compStartSec;
        summary = combineMoveSummaries(summary, moveContextByDelta(context, deltaSec));
    }

    return { summary, anchorToken: sharedAnchor.token, error: null };
};

const requestSemanticAlignmentPlan = async (
    _contexts: ClipAlignmentContext[]
): Promise<SemanticAlignmentPlan | null> => {
    return null;
};

const findSharedAnchor = (
    contexts: ClipAlignmentContext[],
    options: { requireUseful: boolean }
): AnchorMatch | null => {
    if (contexts.length < 2) return null;

    const byClipMaps = new Map<string, Map<string, CandidateWord>>();
    for (const context of contexts) {
        const tokenToWord = new Map<string, CandidateWord>();
        for (const candidate of context.candidates) {
            if (options.requireUseful && !candidate.useful) continue;
            if (!tokenToWord.has(candidate.token)) {
                tokenToWord.set(candidate.token, candidate);
            }
        }
        byClipMaps.set(context.clipId, tokenToWord);
    }

    const firstClipTokens = byClipMaps.get(contexts[0].clipId);
    if (!firstClipTokens || firstClipTokens.size === 0) return null;

    const sharedTokens = Array.from(firstClipTokens.keys()).filter((token) => {
        for (let index = 1; index < contexts.length; index++) {
            const mapForClip = byClipMaps.get(contexts[index].clipId);
            if (!mapForClip || !mapForClip.has(token)) return false;
        }
        return true;
    });

    if (sharedTokens.length === 0) return null;

    let bestMatch: AnchorMatch | null = null;
    let bestMaxOrder = Number.POSITIVE_INFINITY;
    let bestSpreadSec = Number.POSITIVE_INFINITY;
    let bestAvgCompSec = Number.POSITIVE_INFINITY;

    for (const token of sharedTokens) {
        const matchesByClipId: Record<string, CandidateWord> = {};
        let maxOrder = 0;
        let minComp = Number.POSITIVE_INFINITY;
        let maxComp = Number.NEGATIVE_INFINITY;
        let sumComp = 0;

        let valid = true;
        for (const context of contexts) {
            const candidate = byClipMaps.get(context.clipId)?.get(token);
            if (!candidate) {
                valid = false;
                break;
            }
            matchesByClipId[context.clipId] = candidate;
            maxOrder = Math.max(maxOrder, candidate.order);
            minComp = Math.min(minComp, candidate.compStartSec);
            maxComp = Math.max(maxComp, candidate.compStartSec);
            sumComp += candidate.compStartSec;
        }

        if (!valid) continue;
        const spreadSec = maxComp - minComp;
        const avgCompSec = sumComp / contexts.length;

        const isBetter =
            maxOrder < bestMaxOrder ||
            (maxOrder === bestMaxOrder && spreadSec < bestSpreadSec) ||
            (maxOrder === bestMaxOrder && spreadSec === bestSpreadSec && avgCompSec < bestAvgCompSec);

        if (isBetter) {
            bestMatch = { token, matchesByClipId };
            bestMaxOrder = maxOrder;
            bestSpreadSec = spreadSec;
            bestAvgCompSec = avgCompSec;
        }
    }

    return bestMatch;
};

export const SequenceMultiSelectPropertiesBar: React.FC = observer(() => {
    const { GetTranscript } = useAssetStore();
    const selectedClipIds = SequenceApplicationStore.selectedClipIds ?? [];
    const [isAligning, setIsAligning] = useState(false);
    const [statusText, setStatusText] = useState<string>("");
    const [statusTone, setStatusTone] = useState<"neutral" | "success" | "error">("neutral");

    const selectedNames = useMemo(
        () => selectedClipIds
            .map((clipId) => SequenceStateStore.getClipTitle(clipId))
            .filter(Boolean),
        [selectedClipIds.join("|")]
    );

    useEffect(() => {
        setStatusText("");
        setStatusTone("neutral");
    }, [selectedClipIds.join("|")]);

    const setError = (message: string) => {
        setStatusTone("error");
        setStatusText(message);
    };

    const handleAlign = useCallback(async () => {
        if (isAligning) return;

        const uniqueClipIds = Array.from(new Set(selectedClipIds.filter(Boolean)));
        if (uniqueClipIds.length < 2) {
            setError("Select at least two clips to align.");
            return;
        }

        const projectId = ProjectAssetsStore.projectId;
        if (!projectId) {
            setError("Project context is missing. Reopen the sequence and try again.");
            return;
        }

        setIsAligning(true);
        setStatusTone("neutral");
        setStatusText("Analyzing transcripts...");

        try {
            const transcriptCache = new Map<string, any>();
            const contexts: ClipAlignmentContext[] = [];
            const failures: string[] = [];

            for (const clipId of uniqueClipIds) {
                const uiClip = SequenceStateStore.clips?.[clipId];
                const clip: any = SequenceCompositionService.getClipById(clipId);
                const displayName = SequenceStateStore.getClipTitle(clipId) || clipId;
                if (!clip || !uiClip) {
                    failures.push(`${displayName}: clip missing`);
                    continue;
                }

                if (!(uiClip.type === "audio" || uiClip.type === "video")) {
                    failures.push(`${displayName}: only audio/video clips can align`);
                    continue;
                }

                const trackId = SequenceStateStore.clipIdToAssetId?.[clipId];
                if (!trackId) {
                    failures.push(`${displayName}: missing track source`);
                    continue;
                }

                const trackRecord = ProjectAssetsStore.projectTracks.find(
                    (track: any) => String(track?.id ?? track?.trackId ?? "") === String(trackId)
                );
                const transcriptId = String(trackRecord?.activeTranscriptId ?? "").trim();
                if (!transcriptId) {
                    failures.push(`${displayName}: no transcript available`);
                    continue;
                }

                let transcriptJson = transcriptCache.get(transcriptId);
                if (!transcriptJson) {
                    transcriptJson = await GetTranscript({ transcriptId, projectId });
                    transcriptCache.set(transcriptId, transcriptJson);
                }

                const transcriptWords = extractTranscriptWords(transcriptJson);
                if (transcriptWords.length === 0) {
                    failures.push(`${displayName}: transcript has no timed words`);
                    continue;
                }

                const candidates = collectCandidateWords(clip, transcriptWords);
                if (candidates.length === 0) {
                    failures.push(`${displayName}: no words intersect this clip`);
                    continue;
                }

                contexts.push({
                    clipId,
                    clip,
                    displayName,
                    trackId: String(trackId),
                    transcriptId,
                    candidates,
                });
            }

            if (contexts.length !== uniqueClipIds.length) {
                const reason = failures[0] ?? "Some selected clips cannot be aligned.";
                setError(`Can't align. ${reason}`);
                return;
            }

            setStatusText("Detecting source groups...");
            const semanticPlan = await requestSemanticAlignmentPlan(contexts);
            const sourceGroups = buildSourceGroups(contexts, semanticPlan);
            if (sourceGroups.length === 0) {
                setError("Can't align. Could not identify source groups.");
                return;
            }

            setStatusText("Aligning clips from the same source...");
            let sameSourceSummary = EMPTY_MOVE_SUMMARY;
            const sourceAnchorTokens: string[] = [];
            for (const group of sourceGroups) {
                const result = alignSameSourceGroup(group);
                if (result.error) {
                    setError(`Can't align. ${result.error}`);
                    return;
                }
                if (result.anchorToken) {
                    sourceAnchorTokens.push(result.anchorToken);
                }
                sameSourceSummary = combineMoveSummaries(sameSourceSummary, result.summary);
            }

            let conversationSummary = EMPTY_MOVE_SUMMARY;
            if (sourceGroups.length > 1) {
                setStatusText("Ordering sources by conversation timing...");
                const profiles: GroupOrderingProfile[] = [];
                for (const group of sourceGroups) {
                    const profile = getGroupOrderingProfile(group);
                    if (!profile) {
                        setError("Can't align. Missing transcript timing for one or more source groups.");
                        return;
                    }
                    profiles.push(profile);
                }

                const orderedProfiles = resolveConversationOrder(profiles, semanticPlan);
                const baselineProfile = orderedProfiles[0];
                if (!baselineProfile) {
                    setError("Can't align. Missing baseline source ordering.");
                    return;
                }

                for (const profile of orderedProfiles) {
                    const desiredCompSec =
                        baselineProfile.compSec + (profile.sourceSec - baselineProfile.sourceSec);
                    const deltaSec = desiredCompSec - profile.compSec;
                    if (!Number.isFinite(deltaSec) || Math.abs(deltaSec) <= EPSILON_MOVE_SEC) continue;
                    for (const context of profile.contexts) {
                        conversationSummary = combineMoveSummaries(
                            conversationSummary,
                            moveContextByDelta(context, deltaSec)
                        );
                    }
                }
            }

            const totalSummary = combineMoveSummaries(sameSourceSummary, conversationSummary);

            if (totalSummary.movedClipCount === 0) {
                setStatusTone("success");
                setStatusText("Already aligned. No clip movement was required.");
                return;
            }

            if (totalSummary.constrainedClipCount > 0) {
                setStatusTone("error");
                setStatusText("Partially aligned. Timeline constraints prevented full alignment for some clips.");
                return;
            }

            setStatusTone("success");
            if (sourceGroups.length > 1) {
                setStatusText(
                    `Aligned ${totalSummary.movedClipCount} clip${totalSummary.movedClipCount === 1 ? "" : "s"} across ${sourceGroups.length} sources.`
                );
            } else {
                const token = sourceAnchorTokens[0];
                if (token) {
                    setStatusText(
                        `Aligned ${totalSummary.movedClipCount} clip${totalSummary.movedClipCount === 1 ? "" : "s"} using "${token}".`
                    );
                } else {
                    setStatusText(
                        `Aligned ${totalSummary.movedClipCount} clip${totalSummary.movedClipCount === 1 ? "" : "s"}.`
                    );
                }
            }
        } catch (error) {
            console.error("[SequenceMultiSelectPropertiesBar] Alignment failed:", error);
            setError("Can't align right now. Please try again.");
        } finally {
            setIsAligning(false);
        }
    }, [GetTranscript, isAligning, selectedClipIds.join("|")]);

    if (selectedClipIds.length <= 1) {
        return null;
    }

    return (
        <Box sx={{height: "100%", width: "100%", display: "flex", flexDirection: "column", gap: "8px", p: "3px"}}>
            <Box sx={PANEL_BASE_SX}>
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.5, py: 0.5}}>
                    <Typography sx={{fontSize: 13, fontWeight: 600}}>Multi-select</Typography>
                    <Typography sx={{fontSize: 12, color: "#6B7280"}}>
                        {selectedClipIds.length} clips
                    </Typography>
                </Box>
                <Box sx={{px: 1, py: 0.5}}>
                    <Typography sx={{fontSize: 13, color: "#111827", mb: 0.75}}>
                        Are these clips from the same source and time?
                    </Typography>
                    <Typography sx={{fontSize: 12, color: "#6B7280", lineHeight: 1.4}}>
                        Align uses transcript timing to sync same-source clips, then keeps sources in conversation order.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleAlign}
                        disabled={isAligning}
                        sx={{
                            mt: 1.25,
                            height: 34,
                            textTransform: "none",
                            borderRadius: "999px",
                            backgroundColor: "#111827",
                            "&:hover": { backgroundColor: "#0B1220" },
                            "&.Mui-disabled": { backgroundColor: "#D1D5DB", color: "#6B7280" },
                            minWidth: 96,
                        }}
                    >
                        {isAligning ? (
                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                <CircularProgress size={14} sx={{color: "#fff"}} />
                                <span>Aligning</span>
                            </Box>
                        ) : (
                            "Align"
                        )}
                    </Button>
                    {statusText && (
                        <Typography
                            sx={{
                                mt: 1,
                                fontSize: 12,
                                color: statusTone === "error" ? "#B91C1C" : statusTone === "success" ? "#065F46" : "#4B5563",
                                lineHeight: 1.4,
                            }}
                        >
                            {statusText}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Box sx={PANEL_BASE_SX}>
                <Box sx={{px: 0.5, py: 0.5}}>
                    <Typography sx={{fontSize: 13, fontWeight: 600, mb: 0.5}}>Selected clips</Typography>
                    <Typography sx={{fontSize: 12, color: "#6B7280", lineHeight: 1.45}}>
                        {selectedNames.slice(0, 4).join(", ")}
                        {selectedNames.length > 4 ? ` +${selectedNames.length - 4} more` : ""}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
});

export default SequenceMultiSelectPropertiesBar;
